import React, { useEffect, useState, useRef } from 'react';
import { Navigation } from './Navigation';
import { Menu, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../UI/Button';
import { ANIMATION_CLASSES, createScrollProgressTracker } from '../../utils/animations';
interface HeaderProps {
  openRegistrationForm?: () => void;
  openPaymentForm?: () => void;
}
export const Header = ({
  openRegistrationForm,
  openPaymentForm
}: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();
  const handleJoinNowClick = () => {
    // Close mobile menu if open
    setMobileMenuOpen(false);
    // Navigate to the access points section on the home page
    if (window.location.pathname === '/') {
      // If already on home page, find and scroll to the access points section
      const accessPointsSection = document.querySelector('section.py-16.bg-gray-50');
      if (accessPointsSection) {
        accessPointsSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else {
      // If on another page, navigate to home and then to access points
      navigate('/?scrollToAccessPoints=true');
    }
  };
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Check URL params for scrollToAccessPoints
  useEffect(() => {
    if (window.location.search.includes('scrollToAccessPoints=true')) {
      setTimeout(() => {
        const accessPointsSection = document.querySelector('section.py-16.bg-gray-50');
        if (accessPointsSection) {
          accessPointsSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 500); // Small delay to ensure the page is fully loaded
    }
  }, []);
  // Close mobile menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);
  // Scroll progress bar
  useEffect(() => {
    const cleanup = createScrollProgressTracker(progress => {
      setScrollProgress(progress);
    });
    return cleanup;
  }, []);
  return <>
      {/* Scroll Progress Bar */}
      <div className="scroll-progress-bar" style={{
      width: `${scrollProgress}%`
    }} role="progressbar" aria-valuenow={Math.round(scrollProgress)} aria-valuemin={0} aria-valuemax={100} aria-label="Page scroll progress" data-id="element-808" />

      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-gray-200 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm py-2' : 'bg-white py-4'}`} data-id="element-809">
        <div className="container mx-auto px-4 flex justify-between items-center" data-id="element-810">
          <div className="flex items-center" data-id="element-811">
            <Link to="/" className="flex items-center focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-secondary rounded-md" aria-label="The Networking Salon Home" data-id="element-812">
              <div className={`transition-all duration-300 ${isScrolled ? 'h-16' : 'h-20'}`} data-id="element-813">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rVLoSkFZL9a8o5YzEX4uvV/Logo_and_Teashirt_branding.png" alt="The Networking Salon Logo" className="h-full w-auto object-contain" width={200} height={80} data-id="element-814" />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4" data-id="element-815">
            <Navigation data-id="element-816" />
            <Button className="ml-4 hover-lift" onClick={handleJoinNowClick} aria-label="Join The Networking Salon Now" data-id="element-817">
              Join Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden" data-id="element-818">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 border border-gray-300 rounded-md hover:border-primary text-secondary hover:text-primary transition-colors focus-visible:ring-2 focus-visible:ring-secondary" aria-expanded={mobileMenuOpen} aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-controls="mobile-menu" data-id="element-819">
              {mobileMenuOpen ? <X size={24} className="animate-scale-in" data-id="element-820" /> : <Menu size={24} className="animate-scale-in" data-id="element-821" />}
            </button>
          </div>
        </div>

        {/* Mobile menu overlay */}
        {mobileMenuOpen && <div id="mobile-menu" className={`md:hidden fixed inset-0 top-[${isScrolled ? '81px' : '113px'}] bg-white z-40 overflow-y-auto ${ANIMATION_CLASSES.slideDown}`} style={{
        top: isScrolled ? '81px' : '113px'
      }} data-id="element-822">
            <div className="container mx-auto px-4 py-6 flex flex-col h-full" data-id="element-823">
              <Navigation isMobile={true} closeMenu={() => setMobileMenuOpen(false)} data-id="element-824" />
              <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-825">
                <Button className="w-full py-4 text-lg shadow-md" onClick={handleJoinNowClick} data-id="element-826">
                  Join Now
                </Button>
              </div>
            </div>
          </div>}
      </header>
    </>;
};